import {
    buildBreadcrumbManager,
    buildDidYouMean,
    buildFacet,
    buildQuerySummary,
    buildRelevanceSortCriterion,
    buildResultList,
    buildResultsPerPage,
    buildSearchBox,
    buildSort,
    loadAdvancedSearchQueryActions,
    loadContextActions,
    loadFacetSetActions,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
    loadSortCriteriaActions,
} from '@coveo/headless';
import { useGeolocationManager } from 'contexts/GeolocationContext';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory } from 'react-router-dom';

import { useSearchParams } from '@/hooks/useSearchParams';
import { trackInteraction } from '@/utils/analytics';
import { preserveHashInUrl } from '@/utils/preserveHashInUrl';

import {
    deriveScenarioKey,
    FAD_BOOST_RELEVANCY_NO_LOCATION,
    FAD_BOOST_SCENARIO_KEYS,
    isVpcQualifyingSearch,
} from '../utils/deriveScenarioKey';
import { buildPreprocessRequest, isInMarketNJ } from './buildPreprocessRequest';
import { useProviderOutsideMatchesContext } from './ProviderOutsideMatchesContext';
import { useProviderSetStateContext } from './ProviderStateContext';

/**
 * Helper function to extract URL parameters used for search queries with geolocation fallback when none provided
 * @param {*} param0
 * @param {*} param0.searchParams - URLSearchParams instance
 * @param {*} param0.currentGeolocation - Current geolocation object
 * @returns {Object} - Object containing the extracted search parameters
 */
const getSearchParamsWithGeolocation = ({ searchParams, currentGeolocation }) => {
    const urlParamQueryNames = [
        ...Object.keys(mapUrlParamsToQuery),
        ...Object.keys(mapUrlParamsToAdvancedQuery),
        ...Object.keys(mapUrlParamsToQueryDistance),
        ...Object.keys(mapUrlParamsToQueryZipCity),
        ...Object.keys(mapUrlParamsToSort),
    ];

    const values = {};

    // Extract single values
    for (const name of urlParamQueryNames) {
        values[name] = searchParams.get(name);
    }

    // Extract multiple values
    for (const name of urlParamFilterNames) {
        values[name] = searchParams.getAll(name);
    }

    // Add geolocation fallback if not in URL
    if (!values['zip-code'] && !values['city'] && currentGeolocation) {
        if (
            currentGeolocation.zipCode &&
            currentGeolocation.zipCode === currentGeolocation.recentSearch
        ) {
            values['zip-code'] = currentGeolocation.zipCode;
        } else if (
            currentGeolocation.city &&
            currentGeolocation.city === currentGeolocation.recentSearch
        ) {
            values['city'] = currentGeolocation.city;
        }
    }

    return values;
};

const defaultValue = {
    coveo: {
        engine: null,
        controller: {
            searchBox: null,
            querySummary: null,
            resultList: null,
            sort: null,
            facet: { vpc: null },
        },
        action: {
            updateQuery: null,
            executeSearch: null,
        },
    },
    sortCriteria: null,
    patientType: 'new',
    handleScheduling: () => {},
    handleSubmit: () => {},
    handleSingleFilter: () => {},
    handleMultipleFilters: () => {},
    handleFilterResetAll: () => {},
    triggerExecuteSearch: () => {},
    handleSortChange: () => {},
    computedSortLabel: null,
    noResultsComponent: null,
    errorComponent: null,
    vpcBannerComponent: null,
    enableTypeahead: true,
    disableCalendar: false,
    disableAvailabilityMessage: false,
    disableNextAvailableSort: false,
    searchboxId: null,
};

export const sortCriteria = [
    ['Relevancy', [buildRelevanceSortCriterion()]],
    ['Next Available', [buildRelevanceSortCriterion()]],
];

export const mapUrlParamsToQuery = {
    term: (v) => v,
};

export const mapUrlParamsToAdvancedQuery = {
    new: (v) => (v === 'true' ? '@acceptingnewpatients = True' : ''),
    // AHSMKTG-3534: Schedule Online filter requires firstavailabilitydate.
    schedule: (v) => (v === 'true' ? '@onlinebookingavailable = True @firstavailabilitydate' : ''),
    cet: (v) => (v ? `@affiliationrank = ${v}` : ''),
    vpc: (v) => (v === 'true' ? '@hasvirtualprimarycare = True' : ''),
};

export const mapUrlParamsToQueryDistance = {
    distance: (v) => (v && v > 0 ? `@mydistanceinmiles <= ${v}` : ''),
};

export const mapUrlParamsToQueryZipCity = {
    'zip-code': (v) => (v ? `@locationzip == ${v}` : ''),
    city: (v) => {
        const arr = v?.split(',');
        return v ? `@locationcity == "${arr[0]}" AND @locationstate == ${arr[arr.length - 1]}` : '';
    },
};

export const mapContextToQueryZipCity = {
    'zip-code': (v) => (v ? [{ contextKey: 'zip', contextValue: v }] : []),
    city: (v) => {
        const arr = v?.split(',');
        return v
            ? [
                  { contextKey: 'city', contextValue: arr[0] },
                  { contextKey: 'state', contextValue: arr[arr.length - 1] },
              ]
            : [];
    },
};

export const mapUrlParamsToSort = {
    sort: (v) => sortCriteria.find(([name]) => name === v)?.[1] ?? sortCriteria[0][1],
};

export const urlParamFilterNames = [
    'specialty',
    'insurance',
    'language',
    'gender',
    'type',
    'visit',
    'affiliation',
    'specialtyservice',
];

export const allUrlParamNames = [
    ...Object.keys(mapUrlParamsToQuery),
    ...Object.keys(mapUrlParamsToAdvancedQuery),
    ...Object.keys(mapUrlParamsToQueryDistance),
    ...Object.keys(mapUrlParamsToQueryZipCity),
    ...Object.keys(mapUrlParamsToSort),
    ...urlParamFilterNames,
];

const removeUrlParams = (searchParams, exclude = []) => {
    const newSearchParams = new URLSearchParams(searchParams);

    const excludesEntries = [];
    for (const name of exclude) {
        const value = newSearchParams.getAll(name).filter(Boolean);
        excludesEntries.push([name, value]);
    }
    for (const name of allUrlParamNames) {
        newSearchParams.delete(name);
    }

    for (const [name, value] of excludesEntries) {
        newSearchParams.append(name, value);
    }

    return newSearchParams;
};

const getSearchUrlParams = (history) => {
    let params = new URLSearchParams(history.location.search);
    params.delete('outsideMatches');
    return params;
};

const ProviderSearchContext = createContext(defaultValue);
const ProviderSearchZipCityContext = createContext({ zipCode: '', city: '' });
const ProviderSearchZipCodeErrorContext = createContext(false);

// Analytics rankingMode values — Phase 2 (parent spec §12.4).
// logResultsSort() takes no arguments in Coveo Headless 2.x; rankingMode is attached
// via analyticsClientMiddleware. AHSMKTG-4136 Fix 1: rankingMode is injected on every
// analytics event (the actionCause === 'resultsSort' guard is removed) — see AC-16.
const RANKING_MODE_RELEVANCY = 'relevancy';
const RANKING_MODE_NEXT_AVAILABLE = 'nextAvailable';

// Default number of results to show per page
export const RESULTS_PER_PAGE = 20;

export const ProviderSearchProvider = (props) => {
    const {
        id,
        children,
        coveo,
        noResultsComponent,
        errorComponent,
        vpcBannerComponent,
        enableTypeahead,
        disableCalendar,
        disableAvailabilityMessage,
        disableNextAvailableSort,
        boostRulesets,
    } = props;

    const [zipCodeServerError, setZipCodeServerError] = useState(false);

    const history = useHistory();
    const searchParams = useSearchParams();

    const setState = useProviderSetStateContext();
    const { setHasOutsideMatches, isVpcOutsideMatches, setIsVpcOutsideMatches, setVpcResults } =
        useProviderOutsideMatchesContext();
    // null until first applyDefaultSort run; read by handleSortChange and analyticsClientMiddleware.
    const selectedSort = useRef(null);
    // Derived and stored before every executeSearch dispatch; read by getScenarioKey() at
    // request time — no closure staleness risk (spec §2, §3 triggerExecuteSearch step 3).
    // Seeded from URL params on mount so interfaceLoad uses the correct ranking functions
    // when a location is already present in the URL (SSR environments fire interfaceLoad
    // before triggerExecuteSearch has a chance to update this ref).
    const currentScenarioKeyRef = useRef(
        (() => {
            const urlZip = searchParams.get('zip-code');
            const urlCity = searchParams.get('city');
            if (!urlZip && !urlCity) return FAD_BOOST_RELEVANCY_NO_LOCATION;
            // Read the cached geolocation from localStorage (same source as currentGeolocationRef)
            // to derive isNJ at mount. preprocessRequest reads geolocation the same way, so this
            // keeps the scenario key aligned with the VPC OR clause on the interfaceLoad search.
            let mountGeo = urlCity ? { city: urlCity } : null;
            if (!mountGeo && urlZip) {
                try {
                    const cached = JSON.parse(localStorage.getItem('ahs-my-geolocation') || 'null');
                    if (cached?.zipCode === urlZip) mountGeo = cached;
                } catch {
                    // localStorage unavailable (private mode, SSR) — isNJ falls back to false
                }
            }
            return deriveScenarioKey(
                'Relevancy',
                true,
                !!searchParams.get('outsideMatches'),
                searchParams.get('term'),
                searchParams.getAll('specialty'),
                isInMarketNJ(mountGeo),
            );
        })(),
    );
    // undefined sentinel: distinguishes "never computed" from a null blank-term search (v1.5.1).
    const lastDefaultSortTermRef = useRef(undefined);
    // Stable ref so the preprocessRequest getter always reads the current value without
    // rebuilding the engine when the prop reference changes.
    const boostRulesetsRef = useRef(boostRulesets);
    boostRulesetsRef.current = boostRulesets;

    // Stable ref for isVpcOutsideMatches so the preprocessRequest getter (built in useMemo)
    // always reads the current value without rebuilding the engine (AHSMKTG-4019).
    // Synced via useEffect (not the render body) so that an explicit reset
    // (isVpcOutsideMatchesRef.current = false in triggerSearch) survives the re-render
    // that React flushes after triggerExecuteSearch completes — the render-body assignment
    // would overwrite the reset before the engine.dispatch calls from useMemo's factory
    // re-run (line ~481) fire preprocessRequest via the test mock.
    const isVpcOutsideMatchesRef = useRef(isVpcOutsideMatches);

    // Stores the most-recent urlParams so getIsVpcQualifyingSearch() reads current
    // term/specialty at Coveo request time without requiring the engine to be rebuilt.
    // Seeded with zip-code/city from the URL so getHasLocation() returns true for the
    // interfaceLoad search when a location is already present in the URL on mount.
    const currentUrlParamsRef = useRef({
        'zip-code': searchParams.get('zip-code'),
        city: searchParams.get('city'),
    });

    // Indicate if the initial search has been completed
    // This is used for the first load when the page is opened with URL params
    // To scroll to the active provider card and clean up URL params on subsequent searches
    const isInitializationComplete = useRef(false);

    const { currentGeolocation, resolveGeolocation } = useGeolocationManager();
    const currentGeolocationRef = useRef(currentGeolocation);

    // Track the last search parameters to prevent infinite loops
    const lastSearchParamsRef = useRef(null);
    const [zipCode, setZipCode] = useState(
        searchParams.get('zip-code') || currentGeolocation?.zipCode,
    );
    const [city, setCity] = useState(searchParams.get('city') || currentGeolocation?.city);
    const pageNumber = searchParams.get('page') || 1;
    const resultsPerPage = pageNumber * RESULTS_PER_PAGE;

    const patientType = searchParams.get('patient-type') || 'new';

    const [queryCorrected, setQueryCorrected] = useState(false);
    const [computedSortLabel, setComputedSortLabel] = useState(null);

    const coveoAnalyticsRef = useRef({ event: '', payload: {}, shouldReset: true });
    const engine = useMemo(() => {
        if (!coveo) {
            return null;
        }

        return getHeadlessEngine(`provider-search-${id}`, {
            ...coveo,
            search: { searchHub: 'FindaDoctor' },
            preprocessRequest: buildPreprocessRequest({
                getGeolocation: () => currentGeolocationRef.current,
                getScenarioKey: () => currentScenarioKeyRef.current,
                getBoostRulesets: () => boostRulesetsRef.current,
                // AHSMKTG-4019: VPC outside-matches exclusion filter gate.
                // Read via ref so the getter always reflects the current value without
                // requiring the engine to be rebuilt.
                getIsVpcOutsideMatches: () => isVpcOutsideMatchesRef.current,
                // AHSMKTG-4019: VPC inclusion clause qualifying-search gate (R-01 / BRs).
                // Reads the most-recent urlParams stored in triggerExecuteSearch.
                getIsVpcQualifyingSearch: () =>
                    isVpcQualifyingSearch({
                        term: currentUrlParamsRef.current?.term,
                        specialties: currentUrlParamsRef.current?.specialty,
                    }),
                // AHSMKTG-4019: outside-matches pass gate for Scenario 4 VPC exclusion (AC-10).
                // When the outside-matches pass fires for non-NJ, the NOT clause must be appended
                // to suppress VPCs that surface when the radius filter is removed.
                getIsOutsideMatches: () => !!currentUrlParamsRef.current?.isOutsideMatches,
                // TC-7.1 fix: suppress the VPC OR-clause when the URL has no location params.
                // When hasLocation is false the cached geo (currentGeolocationRef.current) may
                // still be an NJ geo from a prior search, causing isInMarketNJ to return true
                // on interfaceLoad / Clear-All paths even though no zip-code or city is active.
                getHasLocation: () =>
                    !!(
                        currentUrlParamsRef.current?.['zip-code'] ||
                        currentUrlParamsRef.current?.city
                    ),
            }),
            analytics: {
                analyticsMode: 'legacy',
                // Phase 2 (Q-03 resolution): logResultsSort() in Coveo Headless 2.x takes no
                // arguments for customData. The middleware approach is the correct mechanism —
                // intercept every analytics event and attach rankingMode before it reaches the
                // Coveo platform. selectedSort is a stable ref; .current is read at event time.
                // AHSMKTG-4136 Fix 1: rankingMode is injected on every event, not just
                // resultsSort — the actionCause guard is removed (AC-16).
                analyticsClientMiddleware: (eventType, payload) => {
                    if (!payload) {
                        return payload;
                    }
                    const rankingMode =
                        selectedSort.current === 'Next Available'
                            ? RANKING_MODE_NEXT_AVAILABLE
                            : RANKING_MODE_RELEVANCY;
                    return {
                        ...payload,
                        customData: { ...(payload.customData || {}), rankingMode },
                    };
                },
            },
        });
    }, [coveo, id]);

    // Keep isVpcOutsideMatchesRef in sync with the context state value. Placed in a
    // useEffect (not the render body) so that an explicit reset performed in triggerSearch
    // (isVpcOutsideMatchesRef.current = false) survives the re-render that follows
    // triggerExecuteSearch: during the re-render the render body would have overwritten
    // the reset, causing the useMemo factory's engine.dispatch call (line ~481) to see
    // the stale true value via preprocessRequest's getIsVpcOutsideMatches getter.
    // In production, preprocessRequest is only called for executeSearch HTTP requests —
    // never for non-search internal dispatches — so the one-render-cycle lag introduced
    // by useEffect is semantically correct (AHSMKTG-4019).
    useEffect(() => {
        isVpcOutsideMatchesRef.current = isVpcOutsideMatches;
    }, [isVpcOutsideMatches]);

    // Keep currentGeolocationRef in sync with currentGeolocation state so that
    // preprocessRequest's getGeolocation() getter always returns the latest resolved
    // coordinates, even when GeolocationContext resolves lat/lng asynchronously between
    // triggerExecuteSearch calls (AHSMKTG-4184). The existing assignment inside
    // triggerExecuteSearch (currentGeolocationRef.current = geolocation) is kept as the
    // authoritative update path for searches that resolve fresh geolocation data; this
    // useEffect is a secondary sync that covers upstream state changes.
    useEffect(() => {
        currentGeolocationRef.current = currentGeolocation;
    }, [currentGeolocation]);

    // Mount-time validation: warn for each missing FAD_BOOST_* key so misconfiguration
    // surfaces in the console. Missing keys fall back to [] (Coveo default pipeline, AC-13).
    useEffect(() => {
        FAD_BOOST_SCENARIO_KEYS.forEach((key) => {
            if (!boostRulesets || !Object.prototype.hasOwnProperty.call(boostRulesets, key)) {
                // eslint-disable-next-line no-console
                console.warn(`[ProviderSearchContext] Missing boostRulesets key: ${key}`);
            }
        });
    }, [boostRulesets]);

    const value = useMemo(() => {
        if (!engine) {
            return defaultValue;
        }
        const searchboxId = `provider-searchbox-${id}`;

        const QueryActionCreators = loadQueryActions(engine);
        const AdvancedSearchActionCreators = loadAdvancedSearchQueryActions(engine);
        const SortCriteriaActions = loadSortCriteriaActions(engine);
        const FacetSetActions = loadFacetSetActions(engine);
        const SearchAnalyticsActionCreators = loadSearchAnalyticsActions(engine);
        const SearchActionCreators = loadSearchActions(engine);
        const ContextActionCreators = loadContextActions(engine);

        const controllerSearchBox = enableTypeahead
            ? buildSearchBox(engine, {
                  options: { numberOfSuggestions: 10, id: `provider-searchbox-${id}` },
              })
            : null;
        const controllerQuerySummary = buildQuerySummary(engine);
        const controllerDidYouMean = buildDidYouMean(engine, {
            options: {
                automaticallyCorrectQuery: false,
            },
        });
        const controllerResultList = buildResultList(engine, {
            options: {
                fieldsToInclude: [
                    'affiliationrank',
                    'entitytype',
                    'permanentid',
                    'filetype',
                    'affiliation',
                    'location',
                    'overallrating',
                    'body',
                    'abouttheprovider',
                    'degreesname',
                    'firstname',
                    'fullname',
                    'gender',
                    'hospitalaffiliations',
                    'specialties',
                    'acceptingnewpatients',
                    'conditions',
                    'primaryspecialty',
                    'imageurl',
                    'insuranceaccepted',
                    'language',
                    'lastname',
                    'locationcitystate',
                    'locationcity',
                    'locationstate',
                    'locationzip',
                    'locationphone',
                    'latitude',
                    'longitude',
                    'providertype',
                    'additionalvisittypes',
                    'languages',
                    'onlinebookingavailable',
                    'firstavailabilitydate',
                    'nextavailabilitydate',
                    'availableslots',
                    'providernpi',
                    'practicegroups',
                    'mycharturl',
                    'locepicdepartmentid',
                    'availableslots',
                    'availablesoononline',
                    'availableonline',
                    'availableoffline',
                    'specialtyservices',
                    'hasvirtualprimarycare',
                    'locaddressoverridemessage',
                ],
            },
        });

        const resultsPerPageController = buildResultsPerPage(engine, {
            initialState: { numberOfResults: resultsPerPage },
        });

        const urlSortParam = searchParams.get('sort');
        const initialCriterion = urlSortParam
            ? mapUrlParamsToSort.sort(urlSortParam) ?? sortCriteria[0][1]
            : sortCriteria[0][1];
        const controllerSort = buildSort(engine, {
            initialState: { criterion: initialCriterion },
        });

        /**
         * Factory function to create a facet controller
         * @param {string} facetId - The id of the facet
         * @param {string} field - The field to facet on
         * @param {string} resultsMustMatch - Whether ALL (allValues) or ANY (atLeastOneValue) values must match (default: 'allValues')
         * @returns
         */
        const factoryFacet = (facetId, field, resultsMustMatch, injectionDepth) => {
            return buildFacet(engine, {
                options: {
                    injectionDepth: injectionDepth || 1000,
                    facetId,
                    field,
                    numberOfValues: 999999,
                    resultsMustMatch: resultsMustMatch || 'allValues',
                    sortCriteria: 'alphanumeric',
                },
            });
        };

        const controllerFacetSpecialty = factoryFacet(
            'specialty',
            'specialties',
            'atLeastOneValue',
            5000,
        );
        const controllerFacetInsurance = factoryFacet('insurance', 'insuranceaccepted');
        const controllerFacetLanguage = factoryFacet('language', 'languages');
        const controllerFacetGender = factoryFacet('gender', 'gender', 'atLeastOneValue');
        const controllerFacetType = factoryFacet('type', 'providertype', 'atLeastOneValue');
        const controllerFacetVisit = factoryFacet('visit', 'additionalvisittypes');
        const controllerFacetVpc = factoryFacet('vpc', 'hasvirtualprimarycare');
        const controllerFacetAffiliation = factoryFacet('affiliation', 'hospitalaffiliations');
        const controllerFacetSpecialtyService = factoryFacet(
            'specialtyservice',
            'specialtyservices',
        );
        engine.dispatch(FacetSetActions.updateFacetAutoSelection({ allow: true }));

        const controllerBreadcrumbManager = buildBreadcrumbManager(engine);

        /**
         * Resolves the Coveo analytics action for the given event type.
         * Used by triggerExecuteSearch to build the executeSearch dispatch.
         * For sort changes: event === 'resultsSort' → logResultsSort() (AC-15).
         */
        const executeSearch = (event, payload = {}) => {
            const {
                logInterfaceLoad,
                logSearchboxSubmit,
                logResultsSort,
                logQuerySuggestionClick,
                logFacetSelect,
                logFacetDeselect,
                logFacetBreadcrumb,
                logClearBreadcrumbs,
                logStaticFilterSelect,
                logStaticFilterDeselect,
                logStaticFilterClearAll,
                logInterfaceChange,
            } = SearchAnalyticsActionCreators;
            switch (event) {
                case 'resultsSort':
                    return SearchActionCreators.executeSearch(logResultsSort());
                case 'patientTypeChange':
                    return SearchActionCreators.executeSearch(logInterfaceChange());
                case 'searchboxSubmit':
                    return SearchActionCreators.executeSearch(logSearchboxSubmit());
                case 'querySuggestionClick':
                    return SearchActionCreators.executeSearch(logQuerySuggestionClick(payload)); // LogQuerySuggestionClickActionCreatorPayload {id: string, suggestion: string}
                case 'facetSelect':
                    return SearchActionCreators.executeSearch(logFacetSelect(payload)); // LogFacetSelectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetDeselect':
                    return SearchActionCreators.executeSearch(logFacetDeselect(payload)); // LogFacetDeselectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetBreadcrumb':
                    return SearchActionCreators.executeSearch(logFacetBreadcrumb(payload)); // LogFacetBreadcrumbActionCreatorPayload {facetId: string, facetValue: string}
                case 'clearBreadcrumbs':
                    return SearchActionCreators.executeSearch(logClearBreadcrumbs());
                case 'staticFilterSelect':
                    return SearchActionCreators.executeSearch(logStaticFilterSelect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterDeselect':
                    return SearchActionCreators.executeSearch(logStaticFilterDeselect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterClearAll':
                    return SearchActionCreators.executeSearch(logStaticFilterClearAll(payload)); // LogStaticFilterClearAllActionCreatorPayload {staticFilterId: string}
                default:
                    return SearchActionCreators.executeSearch(logInterfaceLoad());
            }
        };

        /**
         * Sets selectedSort.current and dispatches updateSortCriterion so Coveo uses the
         * correct sort on the next search request.
         *
         * Algorithm (spec §3 applyDefaultSort — Phase 2, v1.5.1):
         *   !urlParams.sort → term-change gate: only recomputes when urlParams.term changed.
         *                     compute default: !hasLocation && !disableNextAvailableSort
         *                     → 'Next Available'; else → 'Relevancy'.
         *   urlParams.sort  → use the explicit sort param; dispatch to confirm the criterion.
         *
         * Phase 2 change: the `!term` gate is removed — term no longer prevents Next Available.
         * The lastDefaultSortTermRef gate ensures filter-change calls (location, specialty, etc.)
         * never override an established sort — only term changes trigger a recomputation.
         */
        const applyDefaultSort = (urlParams) => {
            const { 'zip-code': zipCodeParam, city: cityParam } = urlParams;
            const hasLocation = zipCodeParam || cityParam;

            if (!urlParams.sort) {
                if (urlParams.term !== lastDefaultSortTermRef.current) {
                    const defaultSort =
                        !hasLocation && !disableNextAvailableSort ? 'Next Available' : 'Relevancy';
                    selectedSort.current = defaultSort;
                    lastDefaultSortTermRef.current = urlParams.term;
                    engine.dispatch(
                        SortCriteriaActions.updateSortCriterion(
                            mapUrlParamsToSort.sort(defaultSort),
                        ),
                    );
                    setComputedSortLabel(defaultSort);
                }
            } else {
                selectedSort.current = urlParams.sort;
                engine.dispatch(
                    SortCriteriaActions.updateSortCriterion(
                        mapUrlParamsToSort.sort(urlParams.sort),
                    ),
                );
            }
        };

        const triggerExecuteSearch = async (urlParams) => {
            currentUrlParamsRef.current = urlParams;
            setState((prevState) => {
                if (prevState === 'default') {
                    coveoAnalyticsRef.current.shouldReset = false;
                }
                return 'results';
            });

            /**
             * After the first search triggered by the ProviderSearchProvider
             * clean up the URL by removing activeProvider and page parameters
             */
            if (isInitializationComplete.current) {
                const url = new URL(window.location);
                url.searchParams.delete('activeProvider');
                url.searchParams.delete('page');
                // Intentionaly replace the url state outside of the react-router history to avoid re-render and an extra search
                window.history.replaceState({}, '', url);
            }

            const { 'zip-code': zipCodeParam, city: cityParam } = urlParams;

            setZipCodeServerError(false);

            // Update geolocation with consolidated logic
            const geolocation = await resolveGeolocation({
                zipCode: zipCodeParam,
                city: cityParam,
            });

            currentGeolocationRef.current = geolocation;

            if ((zipCodeParam || cityParam) && !geolocation) {
                setZipCodeServerError(true);
                return;
            }

            setZipCode(geolocation?.zipCode || '');
            setCity(geolocation?.city || '');

            const queryArray = [];
            for (const name in mapUrlParamsToQuery) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQuery, name)) {
                    const fn = mapUrlParamsToQuery[name];
                    queryArray.push(fn(urlParams[name]));
                }
            }
            const query = queryArray.filter(Boolean).join(' AND ');

            const zipCityQueryArray = [];

            if (zipCodeParam || cityParam) {
                // If there are zip-code or city URL params, use them first to set the zip/city part of the query
                for (const name in mapUrlParamsToQueryZipCity) {
                    if (Object.hasOwnProperty.call(mapUrlParamsToQueryZipCity, name)) {
                        const fn = mapUrlParamsToQueryZipCity[name];
                        zipCityQueryArray.push(fn(urlParams[name]));
                    }
                }
            } else if (zipCityQueryArray.filter(Boolean).length === 0 && geolocation != null) {
                // If not, and if geolocation is available, use it to set the zip/city part of the query
                if (geolocation.zipCode) {
                    const fn = mapUrlParamsToQueryZipCity['zip-code'];
                    zipCityQueryArray.push(fn(geolocation.zipCode));
                } else if (geolocation.city) {
                    const fn = mapUrlParamsToQueryZipCity['city'];
                    zipCityQueryArray.push(fn(geolocation.city));
                }
            }

            const zipCityQuery = zipCityQueryArray.filter(Boolean).join(' AND ');

            const distanceQueryArray = [];
            for (const name in mapUrlParamsToQueryDistance) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQueryDistance, name)) {
                    const fn = mapUrlParamsToQueryDistance[name];
                    distanceQueryArray.push(fn(urlParams[name]));
                }
            }

            if (distanceQueryArray.filter(Boolean).length > 0) {
                distanceQueryArray.push(zipCityQuery);
            }

            const distanceQuery = distanceQueryArray
                .filter(Boolean)
                .map((q, index) => (index > 0 ? `(${q})` : q))
                .join(' OR ');

            const advancedQueryArray = [];
            for (const name in mapUrlParamsToAdvancedQuery) {
                if (Object.hasOwnProperty.call(mapUrlParamsToAdvancedQuery, name)) {
                    const fn = mapUrlParamsToAdvancedQuery[name];
                    advancedQueryArray.push(fn(urlParams[name]));
                }
            }
            // TC-17.1 fix (v2.1.0 — inverted, AC-20): the location AQ fires only when
            // distance > 0. distanceQuery already embeds zipCityQuery via the OR join
            // when distance > 0 (see distanceQueryArray construction above); when distance
            // is null/0 ("Any" selected in the distance dropdown, Clear-All stripped the
            // param, or direct navigation with no distance) distanceQuery is '' and no
            // @locationzip/@locationcity/@locationstate clause is sent — an Any-distance
            // search must not be restricted to a single city (regression: v1.12.0's
            // zipCityQuery fallback incorrectly narrowed "Any" results to one city).
            advancedQueryArray.push(distanceQuery);
            const advancedQuery = advancedQueryArray
                .filter(Boolean)
                .map((q, index) => (index > 0 ? `(${q})` : q))
                .join(' AND ');

            const hasLocation = zipCodeParam || cityParam;

            // Ensure selectedSort.current is correct for this search and dispatch
            // updateSortCriterion before executeSearch (spec §3 triggerExecuteSearch step 2).
            applyDefaultSort(urlParams);

            let contextQueryArray = [];
            for (const name in mapContextToQueryZipCity) {
                if (Object.hasOwnProperty.call(mapContextToQueryZipCity, name)) {
                    const fn = mapContextToQueryZipCity[name];
                    contextQueryArray = [...contextQueryArray, ...fn(urlParams[name])];
                }
            }
            const contextQuery = contextQueryArray.filter(Boolean);
            const contextKeys = ['zip', 'city', 'state'];
            contextKeys.forEach((key) => {
                const found = contextQuery.find((query) => query?.contextKey === key);
                if (found) {
                    engine.dispatch(ContextActionCreators.addContext(found));
                } else {
                    engine.dispatch(ContextActionCreators.removeContext(key));
                }
            });

            for (const name of urlParamFilterNames) {
                engine.dispatch(FacetSetActions.deselectAllFacetValues(name));

                const values = urlParams[name];
                // Normalize to an array before filtering. getSearchParamsWithGeolocation always
                // supplies these as arrays (searchParams.getAll), but triggerExecuteSearch is
                // also called directly by ProviderResultsList's direct-navigation Scenario 3
                // branch with params read off window.location, where a single-valued param is a
                // scalar string. `values?.filter` is not enough: `?.` only short-circuits on
                // null/undefined, so a present-but-empty param (?insurance=) reached
                // ''.filter(...) and threw "values.filter is not a function" (AHSMKTG-4179).
                const filteredValues = (Array.isArray(values) ? values : [values]).filter(Boolean);

                if (filteredValues.length === 0) {
                    continue;
                }

                filteredValues.forEach((v) => {
                    const [value, numberOfResults] = v.split(';');
                    engine.dispatch(
                        FacetSetActions.toggleSelectFacetValue({
                            facetId: name,
                            selection: {
                                value,
                                numberOfResults: parseInt(numberOfResults, 10),
                                state: 'selected',
                            },
                        }),
                    );
                });

                // Updates the updateFreezeCurrentValues flag of a facet to be 'false' to unfreeze Facet Values
                engine.dispatch(
                    FacetSetActions.updateFreezeCurrentValues({
                        facetId: name,
                        freezeCurrentValues: false,
                    }),
                );

                // Increases the number of facet values to 999999 on the search request
                engine.dispatch(
                    FacetSetActions.updateFacetNumberOfValues({
                        facetId: name,
                        numberOfValues: 999999,
                    }),
                );
            }

            engine.dispatch(
                QueryActionCreators.updateQuery({
                    enableQuerySyntax: true,
                    q: `${query}`,
                }),
            );

            engine.dispatch(
                AdvancedSearchActionCreators.registerAdvancedSearchQueries({
                    aq: `${advancedQuery}`,
                }),
            );

            // For non-outsideMatches searches, re-assert the VPC outside-matches ref to
            // false before dispatch. triggerSearch sets isVpcOutsideMatchesRef.current = false
            // before calling this function, but a component re-render flushed by React's act()
            // during the await resolveGeolocation() above can cause the useEffect that syncs
            // the ref to re-fire with the still-true state value, resetting it back to true.
            // Setting it here — after the last re-render opportunity — guarantees
            // preprocessRequest's getIsVpcOutsideMatches() reads false for clean new searches
            // (AHSMKTG-4019).
            if (!urlParams['isOutsideMatches']) {
                isVpcOutsideMatchesRef.current = false;
            }

            // Derive and store the scenario key before dispatch so preprocessRequest reads
            // the correct key from currentScenarioKeyRef.current at request time (spec §3
            // triggerExecuteSearch step 3). isNJ is pre-computed from the resolved geolocation
            // (currentGeolocationRef.current is updated above by resolveGeolocation).
            const isNJ = isInMarketNJ(currentGeolocationRef.current);
            currentScenarioKeyRef.current = deriveScenarioKey(
                selectedSort.current,
                hasLocation,
                urlParams['isOutsideMatches'] || false,
                urlParams.term, // 4th arg — search term (may be undefined)
                urlParams.specialty, // 5th arg — active specialty filter (string[] or undefined)
                isNJ, // 6th arg — NJ in-market gate for Relevancy VPC branch
            );
            engine.dispatch(
                executeSearch(
                    coveoAnalyticsRef?.current?.event,
                    coveoAnalyticsRef?.current?.payload,
                ),
            );

            /**
             * Once the first search has been triggered by the ProviderSearchProvider
             * set the initialization ref to true to indicate that subsequent searches are not the initial load
             * and set the results per page to the default value
             */
            if (!isInitializationComplete.current) {
                isInitializationComplete.current = true;
                // On a deep return (page > 1) the N-batch result set must be preserved. AHSMKTG-3982
                if (Number(searchParams.get('page') || 1) === 1) {
                    resultsPerPageController.set(RESULTS_PER_PAGE);
                }
            }
        };

        /**
         * Handles a user-initiated sort change (called directly from ProviderFilter).
         * Internalizes setCoveoAnalytics('resultsSort') so callers no longer need it.
         * Writes ?sort= to the URL via history.push — sort is URL state (spec §5, v1.4.0).
         * The URL update triggers useEffect → triggerSearch → triggerExecuteSearch.
         * Invariant: exactly one executeSearch dispatch per call (AC-11). Do NOT also call
         * triggerExecuteSearch here — that would cause a double search.
         *
         * Algorithm (spec §3 handleSortChange):
         *   1. selectedSort.current = sort
         *   2. dispatch updateSortCriterion
         *   3. setCoveoAnalytics('resultsSort')
         *   4. newSearchParams = getSearchUrlParams(history)
         *      newSearchParams.set('sort', sort)
         *      history.push(`${window.location.pathname}?${newSearchParams.toString()}`)
         */
        const handleSortChange = (sort) => {
            selectedSort.current = sort;
            engine.dispatch(SortCriteriaActions.updateSortCriterion(mapUrlParamsToSort.sort(sort)));
            setCoveoAnalytics('resultsSort');
            const newSearchParams = getSearchUrlParams(history);
            newSearchParams.set('sort', sort);
            history.push(
                preserveHashInUrl(`${window.location.pathname}?${newSearchParams.toString()}`),
            );
        };

        const setCoveoAnalytics = (event = '', payload = {}, shouldReset = true) => {
            coveoAnalyticsRef.current.event = event;
            coveoAnalyticsRef.current.payload = payload;
            coveoAnalyticsRef.current.shouldReset = shouldReset;
        };

        const handleSubmit = ({ term, zipCityValue, insuranceValue }) => {
            const currentTerm = searchParams.get('term');
            let newSearchParams = getSearchUrlParams(history);
            const additionalParams = Object.keys(mapUrlParamsToAdvancedQuery);
            const valuesAdvanceQuery = {};

            // If term changed, remove all url params
            if (currentTerm !== term && term !== undefined) {
                newSearchParams = removeUrlParams(newSearchParams);
            }

            for (const name of additionalParams) {
                valuesAdvanceQuery[name] = searchParams.get(name);
            }

            const values = [
                { name: 'term', value: term ?? null },
                {
                    name: 'zip-code',
                    value: !isNaN(zipCityValue) && zipCityValue !== '' ? zipCityValue : null,
                },
                { name: 'city', value: isNaN(zipCityValue) ? zipCityValue : null },
                ...[{ name: 'distance', value: zipCityValue ? '5' : null }],
                ...(insuranceValue ? [{ name: 'insurance', value: insuranceValue }] : []),
                ...Object.entries(valuesAdvanceQuery)
                    .filter(([key, value]) => value)
                    .map(([key, value]) => ({ name: key, value })),
            ];

            values.forEach(({ name, value }) => {
                if (Array.isArray(value) && value.length > 0) {
                    newSearchParams.delete(name);
                    value.forEach((v) => newSearchParams.append(name, v));
                } else if (value || value === '') {
                    newSearchParams.set(name, value);
                } else {
                    newSearchParams.delete(name);
                }
            });

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            const currentPath = window.location.pathname;
            const currentUrl = `${currentPath}${history.location.search}`;

            if (currentUrl === newUrl) {
                return false;
            }
            setQueryCorrected(false);
            history.push(preserveHashInUrl(newUrl));
        };

        const handleApplyCorrection = (automatically) => {
            const newSearchParams = getSearchUrlParams(history);
            newSearchParams.set(
                'term',
                controllerDidYouMean?.state?.queryCorrection?.correctedQuery,
            );
            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;
            setQueryCorrected(automatically);
            history.push(preserveHashInUrl(newUrl));
        };

        const handleSingleFilter = (partial, analytics) => {
            const newSearchParams = getSearchUrlParams(history);
            for (const name in partial) {
                if (Object.hasOwnProperty.call(partial, name)) {
                    const value = partial[name];

                    if (value) {
                        newSearchParams.set(name, value);
                    } else {
                        newSearchParams.delete(name);
                    }
                }
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            // Analytics
            if (analytics) {
                trackInteraction({
                    componentName: 'fad_results_filter_sort_panel',
                    interactionType: 'fad_result_filter',
                    selector: 'filter',
                    allFilters: newSearchParams,
                    ...analytics,
                });
            }

            history.push(preserveHashInUrl(newUrl));
        };

        const handleMultipleFilters = (partial, analytics) => {
            const newSearchParams = getSearchUrlParams(history);
            for (const name in partial) {
                if (Object.hasOwnProperty.call(partial, name)) {
                    const value = partial[name];
                    if (newSearchParams.has(name, value)) {
                        newSearchParams.delete(name, value);
                    } else {
                        const params = newSearchParams.getAll(name);
                        const iname = value?.split(';')[0];
                        const matched = params?.find(
                            (p) => p?.includes(';0') !== -1 && p?.split(';')[0] === iname,
                        );
                        if (matched) {
                            newSearchParams.delete(name, matched);
                        } else {
                            newSearchParams.append(name, value);
                        }
                    }
                }
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            // Analytics
            if (analytics) {
                trackInteraction({
                    componentName: 'fad_results_filter_sort_panel',
                    interactionType: 'fad_result_filter',
                    selector: 'filter',
                    allFilters: newSearchParams,
                    ...analytics,
                });
            }
            history.push(preserveHashInUrl(newUrl));
        };

        const handleFilterResetAll = () => {
            const newSearchParams = removeUrlParams(getSearchUrlParams(history), [
                'term',
                'zip-code',
                'city',
            ]);

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            setCoveoAnalytics('clearBreadcrumbs');

            history.push(preserveHashInUrl(newUrl));
        };

        const handleScheduling = (patientType) => {
            const { logInterfaceChange } = SearchAnalyticsActionCreators;
            const newSearchParams = getSearchUrlParams(history);

            if (patientType === 'existing') {
                newSearchParams.set('patient-type', 'existing');
            } else {
                newSearchParams.delete('patient-type');
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            history.push(preserveHashInUrl(newUrl));

            // coveo tracking event
            engine.dispatch(logInterfaceChange());
        };

        return {
            coveo: {
                engine,
                controller: {
                    searchBox: controllerSearchBox,
                    querySummary: controllerQuerySummary,
                    resultList: controllerResultList,
                    resultsPerPage: resultsPerPageController,
                    sort: controllerSort,
                    facet: {
                        specialty: controllerFacetSpecialty,
                        insurance: controllerFacetInsurance,
                        language: controllerFacetLanguage,
                        gender: controllerFacetGender,
                        type: controllerFacetType,
                        visit: controllerFacetVisit,
                        vpc: controllerFacetVpc,
                        affiliation: controllerFacetAffiliation,
                        specialtyservice: controllerFacetSpecialtyService,
                    },
                    breadcrumbManager: controllerBreadcrumbManager,
                    didYouMean: controllerDidYouMean,
                },
                action: {
                    updateQuery: QueryActionCreators.updateQuery,
                    executeSearch,
                },
            },
            sortCriteria,
            patientType,
            handleScheduling,
            handleSubmit,
            handleSingleFilter,
            handleMultipleFilters,
            handleFilterResetAll,
            triggerExecuteSearch,
            currentGeolocationRef,
            handleSortChange,
            computedSortLabel,
            noResultsComponent,
            errorComponent,
            vpcBannerComponent,
            enableTypeahead,
            disableCalendar,
            disableAvailabilityMessage,
            disableNextAvailableSort,
            setCoveoAnalytics,
            searchboxId,
            handleApplyCorrection,
            queryCorrected,
        };
    }, [
        engine,
        id,
        enableTypeahead,
        patientType,
        noResultsComponent,
        errorComponent,
        vpcBannerComponent,
        disableCalendar,
        disableAvailabilityMessage,
        disableNextAvailableSort,
        queryCorrected,
        computedSortLabel,
        setState,
        resolveGeolocation,
        history,
        searchParams,
        resultsPerPage,
    ]);

    /**
     * Callback to trigger a search based on the current URL parameters
     * and geolocation if available.
     *
     * Algorithm (spec §3 triggerSearch, v1.6.0 — AHSMKTG-4019 VPC enhancement):
     *   1. urlParams = getSearchParamsWithGeolocation(...)
     *   2. isOutsideMatches = searchParams.get('outsideMatches')
     *   3a. if !isOutsideMatches:
     *           Reset VPC state (setIsVpcOutsideMatches(false), setVpcResults([])) before
     *           dispatch so stale Scenario 3 flags do not affect new results.
     *           triggerExecuteSearch(urlParams); setHasOutsideMatches(false)
     *   3b. else if isVpcQualifyingSearch + isInMarketNJ(geoForMarketCheck) + !isVpcOutsideMatches:
     *           VPC direct-navigation first entry: run initial search without outsideMatches flag.
     *           ProviderResultsList inspects results and determines Scenario 2 vs Scenario 3.
     *           !isVpcOutsideMatches guard prevents re-entry on subsequent calls.
     *   3c. else: standard outside-matches search (non-qualifying, non-NJ, or re-entry guard).
     *
     * The outsideMatches distance modifier (16000) is now part of the FAD_BOOST_OUTSIDE_MATCHES_*
     * config — the manual rankingFunctionsModifier override is removed (parent R-04).
     */
    const triggerSearch = useCallback(async () => {
        // Sort is URL state (spec §3 triggerSearch step 1, v1.5.0). getSearchParamsWithGeolocation
        // reads ?sort= from searchParams for user-selected sorts written by handleSortChange.
        // Auto-computed defaults are not in the URL, so applyDefaultSort always recomputes fresh.
        const urlParams = getSearchParamsWithGeolocation({ searchParams, currentGeolocation });
        // 'outsideMatches' is read directly from searchParams (it is excluded from the managed
        // URL param maps and deleted by getSearchUrlParams before any history.push).
        const isOutsideMatches = searchParams.get('outsideMatches');

        if (!isOutsideMatches) {
            // Normal search — reset VPC state before dispatch so stale Scenario 3 flags
            // do not affect new results (spec §3 triggerSearch, AHSMKTG-4019).
            setIsVpcOutsideMatches(false);
            // Sync the ref immediately — React state update is async (enqueued), so the render
            // that would update isVpcOutsideMatchesRef.current has not occurred yet. Without
            // this assignment the preprocessRequest getter reads the stale true value that was
            // set during the previous render and appends the NOT clause to a clean new search.
            isVpcOutsideMatchesRef.current = false;
            setVpcResults([]);
            await value.triggerExecuteSearch(urlParams);
            setHasOutsideMatches(false);
        } else {
            // spec §3 triggerSearch geoForMarketCheck (v1.10.0 AC-18):
            // Use the cached geolocation only when its zipCode matches the current URL zip,
            // preventing stale cached geo from a prior search from being accepted as current in-market geo.
            const geoForMarketCheck =
                urlParams['zip-code'] &&
                currentGeolocationRef.current?.zipCode === urlParams['zip-code']
                    ? currentGeolocationRef.current
                    : urlParams.city
                      ? { city: urlParams.city }
                      : null;
            if (
                isVpcQualifyingSearch({ term: urlParams.term, specialties: urlParams.specialty }) &&
                isInMarketNJ(geoForMarketCheck) &&
                !isVpcOutsideMatches
            ) {
                // VPC-qualifying + NJ, first entry: outsideMatches=true is in the URL but
                // isVpcOutsideMatches is false — run the initial search to inspect results.
                // ProviderResultsList evaluates: allResultsAreVpc → Scenario 3;
                // !allResultsAreVpc → Scenario 2 fallback (clears outsideMatches URL param).
                // The !isVpcOutsideMatches guard prevents re-entry: when ProviderResultsList sets
                // isVpcOutsideMatches=true, the next triggerSearch call falls to the else branch.
                await value.triggerExecuteSearch(urlParams); // normal search — no isOutsideMatches flag
            } else {
                // Non-qualifying, non-NJ, or isVpcOutsideMatches already true (re-entry guard):
                // standard outside-matches behavior — run the outside-matches search.
                await value.triggerExecuteSearch({
                    ...urlParams,
                    distance: 'any',
                    isOutsideMatches: true,
                });
                setHasOutsideMatches(true);
            }
        }

        if (coveoAnalyticsRef?.current?.shouldReset) {
            value.setCoveoAnalytics();
        }
    }, [
        searchParams,
        value,
        setHasOutsideMatches,
        currentGeolocation,
        isVpcOutsideMatches,
        setIsVpcOutsideMatches,
        setVpcResults,
    ]);

    useEffect(() => {
        const urlParams = getSearchParamsWithGeolocation({ searchParams, currentGeolocation });
        const hasLocation = urlParams['zip-code'] || urlParams['city'];

        // Require term to be defined (e.g. empty search), location, or at least one filter
        // (e.g. ?specialty=X from the provider carousel "View All Doctors" CTA)
        const hasFilters = urlParamFilterNames.some(
            (name) => urlParams[name]?.filter(Boolean).length > 0,
        );
        if (urlParams.term === null && !hasLocation && !hasFilters) {
            return;
        }

        // Create a stable key to compare search parameters
        const searchKey = JSON.stringify({
            urlParams,
            //  Add additional keys that would invoke a new search when changed
            context: {
                page: searchParams.get('page'),
                outsideMatches: searchParams.get('outsideMatches'),
            },
        });
        // Only trigger search if parameters have actually changed
        // This prevents infinite loops if the URL parameters are the same
        if (lastSearchParamsRef.current !== searchKey) {
            lastSearchParamsRef.current = searchKey;

            triggerSearch();
        }
    }, [searchParams, value, triggerSearch, currentGeolocation]);

    return (
        <ProviderSearchContext.Provider value={value}>
            <ProviderSearchZipCityContext.Provider value={{ zipCode, city }}>
                <ProviderSearchZipCodeErrorContext.Provider value={zipCodeServerError}>
                    {children}
                </ProviderSearchZipCodeErrorContext.Provider>
            </ProviderSearchZipCityContext.Provider>
        </ProviderSearchContext.Provider>
    );
};

export const useProviderSearchContext = () => {
    const context = useContext(ProviderSearchContext);

    if (context == null) {
        throw new Error(`'useProviderSearchContext' needs to be within 'ProviderSearchContext'`);
    }

    return context;
};

export const useProviderSearchZipCityContext = () => {
    const context = useContext(ProviderSearchZipCityContext);

    if (context == null) {
        throw new Error(
            `'useProviderSearchZipCityContext' needs to be within 'ProviderSearchZipCityContext'`,
        );
    }

    return context;
};

export const useProviderSearchZipCodeErrorContext = () => {
    const context = useContext(ProviderSearchZipCodeErrorContext);

    if (context == null) {
        throw new Error(
            `'useProviderSearchZipCodeErrorContext' needs to be within 'ProviderSearchZipCodeErrorContext'`,
        );
    }

    return context;
};
